import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Section, ArticleCard, Pillar } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import DeepContentBlocks from "../components/DeepContentBlocks";
import { STATIC_HUB_DEEP_CONTENT } from "../config/staticHubDeepContent";
import { Users, Heart, Sparkles, ShieldCheck } from "lucide-react";
import AppCta from "../components/AppCta";

export default function FamiliesPage() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    Promise.all([
      API.get("/content/articles?category=parenting&limit=8"),
      API.get("/content/articles?category=postpartum&limit=8"),
    ]).then(([a1, a2]) => setArticles([...(a1.data.items || []), ...(a2.data.items || [])]));
  }, []);
  const pillars = t("families.pillars", { returnObjects: true });
  const icons = [Users, Heart, ShieldCheck, Sparkles];
  const deep = STATIC_HUB_DEEP_CONTENT.families;
  return (
    <div data-testid="page-families">
      <section className="relative overflow-hidden hero-bg">
        <div className="app-container pt-16 pb-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs tracking-[0.32em] uppercase gold-text" data-testid="families-eyebrow">{t("families.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05] max-w-[18ch]" data-testid="families-title">{t("families.title")}</h1>
            <p className="text-white/75 mt-5 max-w-[52ch] leading-relaxed body-copy">{t("families.subtitle")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/ask-donny" className="btn-primary text-sm" data-testid="families-cta-donny">{t("families.cta_donny")}</Link>
              <Link to="/hub" className="btn-secondary text-sm" data-testid="families-cta-hub">{t("families.cta_hub")}</Link>
            </div>
          </div>
          <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 max-w-md md:ml-auto">
            <img src="https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=900&q=85" alt={t("families.image_alt")} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
      <Section eyebrow={t("families.pillars_eyebrow")} title={t("families.pillars_title")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="families-pillars">
          {pillars.map((p, i) => <Pillar key={i} label={p} icon={icons[i % icons.length]} />)}
        </div>
      </Section>
      <Section eyebrow={t("families.editorial_eyebrow")} title={t("families.editorial_title")} subtitle={t("families.editorial_subtitle")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="families-articles">
          {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
      </Section>
      <DeepContentBlocks
        pageTitle={deep.pageTitle}
        editorial={deep.editorial}
        quotes={deep.quotes}
        products={deep.products}
        askDonnyPrompt={deep.askDonnyPrompt}
        testidPrefix="families-deep"
      />
      <PractitionerRecommendations
        eyebrow={t("marketplace.find_pro_eyebrow")}
        title={t("families.practitioners_title")}
        subtitle={t("families.practitioners_subtitle")}
        topic="family-support"
        limit={3}
        testid="families-practitioner-recs"
      />

      <AppCta variant="families" testid="families-app-cta" />
    </div>
  );
}
